import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { capitalize } from '@core/util/string';
import { EncumbranceTag } from '@core/util/tags';
import { Encumbrance, EncumbranceActionType } from '@models/encumbrance';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { SavedInstrument } from '@models/teranet';
import { Select } from '@ngxs/store';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterUploadedFilesDispatch } from '@state/matter/uploaded-files/matter-uploaded-files.dispatch';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { SaleInstrumentsState } from '@state/sale_instruments';
import { Subscription } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { filter, map, switchMap } from 'rxjs/operators';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';

type DocumentView = 'payout' | 'charge';

const PageButtonGroupOptions: ButtonGroupOption<DocumentView>[] = [
	{ label: 'Payout Statement', value: 'payout' },
	{
		label: 'Charge document',
		value: 'charge',
	},
];

@Component({
	selector: 'app-encumbrance',
	templateUrl: './encumbrance.component.html',
	styleUrls: ['./encumbrance.component.scss'],
	standalone: false,
})
export class EncumbranceComponent implements OnInit, OnDestroy {
	public encumbrance: Encumbrance = {} as Encumbrance;
	public encumbranceActionTypes = EncumbranceActionType;
	public fileType = MatterDocumentType.ENCUMBRANCE;
	public fileName: string;
	public tag: string;
	public latestUploadedFile: MatterFile;
	public instrument: SavedInstrument;
	public instruments: SavedInstrument[];
	public documentView: DocumentView = 'payout';
	public documentViewOptions = PageButtonGroupOptions;
	public encumbranceSubscription: Subscription;
	public matterFileSubscription: Subscription;
	public instrumentsSubscription: Subscription;

	@Select(SaleInstrumentsState.getItems) instruments$: Observable<
		SavedInstrument[]
	>;
	@Select(MatterEncumbrancesState.getEncumbrance)
	encumbranceFilterFn$: Observable<(id: string) => Encumbrance>;
	@Select(MatterUploadedFilesSelectors.getLatestFileByTypeAndSystemTag)
	matterFilesFilterFn$: Observable<
		(type: MatterDocumentType, tag: string) => MatterFile
	>;

	@Select(SelectedMatter.id) matterId$: Observable<string>;

	constructor(
		private activatedRoute: ActivatedRoute,
		private navigationService: NavigationService,
		private matterUploadedFilesDispatch: MatterUploadedFilesDispatch,
	) {}

	ngOnInit(): void {
		const encumbranceObservable = this.activatedRoute.paramMap.pipe(
			map((params) => params.get('encumbranceId')),
			filter((id) => !!id),
			switchMap((id) => this.encumbranceFilterFn$.pipe(map((fn) => fn(id)))),
			filter((encumbrance) => !!encumbrance),
		);

		this.encumbranceSubscription = encumbranceObservable.subscribe(
			(encumbrance) => {
				this.encumbrance = encumbrance;
				this.fileName = this.getFileName(encumbrance);
				this.tag = EncumbranceTag(encumbrance.id);
				this.setInstrument();
			},
		);

		this.matterFileSubscription = encumbranceObservable
			.pipe(
				map((encumbrance) => encumbrance.id),
				switchMap((id) =>
					this.getMatterFile$(
						MatterDocumentType.ENCUMBRANCE,
						EncumbranceTag(id),
					),
				),
			)
			.subscribe((doc) => {
				this.latestUploadedFile = doc;
			});

		this.instrumentsSubscription = this.instruments$.subscribe(
			(_instruments) => {
				this.instruments = _instruments;
				this.setInstrument();
			},
		);
	}

	setInstrument() {
		if (
			this.encumbrance?.registrationNumber?.length > 0 &&
			this.instruments?.length > 0
		) {
			this.instrument = this.instruments.find(
				(x) =>
					x.registration_number?.toLowerCase() ===
					this.encumbrance.registrationNumber?.toLowerCase(),
			);
		} else {
			this.instrument = null;
		}
	}

	getMatterFile$(
		type: MatterDocumentType,
		tag: string,
	): Observable<MatterFile> {
		return this.matterFilesFilterFn$.pipe(map((fn) => fn(type, tag)));
	}

	getFileName(encumbrance: Encumbrance): string {
		return `${capitalize(encumbrance.action)} ${capitalize(encumbrance?.type)}`;
	}

	showPaymentCalculator() {
		this.navigationService.goToUrl('payment-calculator', null, {
			relativeTo: this.activatedRoute,
		});
	}

	delete() {
		this.matterUploadedFilesDispatch.Delete(this.latestUploadedFile.id);
	}

	ngOnDestroy(): void {
		this.encumbranceSubscription?.unsubscribe();
		this.matterFileSubscription?.unsubscribe();
		this.instrumentsSubscription?.unsubscribe();
	}
}
